import React, { memo, PropsWithChildren, useCallback, useRef } from 'react'
import {
  TtButton,
  TtDropdown,
  TtIconButton,
  TtMenu,
  TtMenuItem,
  TtProgressBar,
  TtTrayItem,
  TtTrayPoll,
} from '@23/react'
import { useSortable } from '@dnd-kit/sortable'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'
import { useParams } from 'react-router'
import {
  useOverlayState,
  setChyron,
} from '../../../../firebase/hooks/useOverlays'
// @ts-expect-error — importing js
import { ConfirmComponent } from '../../../../sharedLegacy/components'
import usePollsOverlays from '../../../hooks/usePollsOverlay'
import GutterMenu from '../GutterMenu'
import Toolbar from '../Toolbar'
import useTrayTracking from '../useTrayTracking'
import { useScrollRegister, useScroll } from '../ScrollProvider'
import { type Poll } from '../models/poll'
import {
  useStartPollMutation,
  useEndPollMutation,
  usePollsQuery,
} from '../hooks/use-polls'
import { TrayCardPoll } from '../../../../firebase/model/tray-cards/types/poll-model'
import { ChyronType } from '../../../../firebase/model/chyron-model'
import {
  removeCard,
  setActiveCard,
  updatePollCard,
} from '../../../../firebase/hooks/useTray'

import './PollCard.scss'

///
/// PollCard
///

interface PollCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardPoll
}

const PollCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
  children,
}: PropsWithChildren<PollCardProps>): JSX.Element => {
  const { live_id: liveId } = useParams<{ live_id: string }>()

  const trayTracking = useTrayTracking()
  const ref = useRef<HTMLTtTrayItemElement>(null)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()
  const overlay = useOverlayState()
  const { togglePollOverlay } = usePollsOverlays()
  const { data: polls } = usePollsQuery(liveId)

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const isChyronCurrentlyShowing =
    overlay &&
    card.poll &&
    overlay.type === ChyronType.PollResult &&
    overlay.id === card.poll.id.toString()

  const onCardClickHandler = () => {
    // You should not be able to activate the card or show the poll chyron if the poll is not started.
    if (card.poll && card.poll.started) {
      trayTracking.activated(card.id)

      setActiveCard(card.id)
      scrollIntoView(card.id)
      if (!isChyronCurrentlyShowing) {
        togglePollOverlay(formatPollModelData(card.poll))
      }
    }
  }

  const onPollSelectHandler = (pollId: number) => {
    const poll = polls?.find((p) => p.id === pollId)
    if (poll) {
      updatePollCard(card.id, poll)
    }
  }

  const onRemoveChyronClickHandler = () => {
    setChyron(null)
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      className="poll-card"
      gutterMode={getGutterMode()}
    >
      <TtTrayPoll
        subject={intl.formatMessage({ defaultMessage: 'Poll' })}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        {polls && polls.length > 0 && !card.poll && (
          <TtDropdown slot="toolbar" placement="bottom-start" closeOnSelect>
            <TtIconButton
              slot="trigger"
              library="one/webinars"
              name="polls"
              label="Select poll"
            />
            <TtMenu
              onTt-select={(e) =>
                onPollSelectHandler(parseInt(e.detail.item.value, 10))
              }
            >
              {polls?.map((poll) => (
                <TtMenuItem key={poll.id} value={poll.id.toString()}>
                  {poll.question}
                </TtMenuItem>
              ))}
            </TtMenu>
          </TtDropdown>
        )}

        {card.poll && card.poll.started && (
          <>
            {isChyronCurrentlyShowing ? (
              <TtIconButton
                slot="toolbar"
                library="one/webinars"
                name="visibility-off"
                label="Visibility off"
                onClick={onRemoveChyronClickHandler}
                className="visibility-icon"
              />
            ) : (
              <TtIconButton
                slot="toolbar"
                library="one/webinars"
                name="visibility-on"
                label="Visibility on"
                onClick={onCardClickHandler}
                className="visibility-icon"
              />
            )}
          </>
        )}
        <Toolbar
          cardId={card.id}
          onDeleteClick={() => {
            trayTracking.deleted(card.id)
            removeCard(card.id)
          }}
        />
        <div className="body">{children}</div>
      </TtTrayPoll>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

//
// PollCard.NoPoll
//

const NoPoll = (): JSX.Element => {
  return <FormattedMessage defaultMessage="Select poll to display" />
}

//
// PollCard.Results
//

function getPollState(poll: Poll): 'queued' | 'started' | 'closed' {
  if (poll.startedAt === '' && !poll.started) {
    return 'queued'
  }
  if (poll.startedAt !== '' && poll.started) {
    return 'started'
  }
  return 'closed'
}

interface ResultsProps {
  poll: Poll
}

const PollResults = ({ poll }: ResultsProps): JSX.Element => {
  const { mutate: startPoll, isLoading: isStartLoading } = useStartPollMutation(
    poll.id
  )
  const { mutate: endPoll, isLoading: isEndLoading } = useEndPollMutation(
    poll.id
  )

  const onStartPollClickHandler = () => {
    startPoll()
  }

  const onEndPollClickHandler = () => {
    endPoll()
  }

  const total = poll.options.reduce((acc, option) => {
    return acc + option.count
  }, 0)

  return (
    <div className="results">
      <div>{poll.question}</div>
      {poll.options.map((option) => {
        const percentage =
          total === 0 ? 0 : Math.round((option.count / total) * 100)
        return (
          <div className="result" key={option.id}>
            <div className="option">
              <div>
                {option.option}
                {option.count > 0 && ` (${option.count})`}
              </div>
              <div>{option.count > 0 ? `${percentage}%` : '-'}</div>
            </div>
            <TtProgressBar percentage={percentage} className="p-bar" />
          </div>
        )
      })}
      {
        {
          queued: (
            <TtButton
              color="secondary"
              size="small"
              pill
              loading={isStartLoading}
              onClick={onStartPollClickHandler}
            >
              <FormattedMessage defaultMessage="Start Poll" />
            </TtButton>
          ),
          started: (
            <ConfirmComponent
              onConfirm={onEndPollClickHandler}
              confirmText={intl.formatMessage({
                defaultMessage:
                  'You are about to close this poll. This means that you won’t be able to start it again to collect more votes.',
              })}
              okText={intl.formatMessage({ defaultMessage: 'Yes, close it' })}
              cancelText={intl.formatMessage({ defaultMessage: 'Cancel' })}
            >
              <TtButton
                color="secondary"
                size="small"
                variant="outline"
                pill
                loading={isEndLoading}
              >
                <FormattedMessage defaultMessage="Close Poll" />
              </TtButton>
            </ConfirmComponent>
          ),
          closed: (
            <TtButton
              color="secondary"
              size="small"
              variant="outline"
              pill
              disabled
            >
              <FormattedMessage defaultMessage="Poll Closed" />
            </TtButton>
          ),
        }[getPollState(poll)]
      }
    </div>
  )
}

//
// Helper function for formatting between newer tray data model to the legacy chyron format
//

function formatPollModelData(poll: NonNullable<TrayCardPoll['poll']>) {
  return {
    poll_id: poll.id.toString(),
    question: poll.question,
    opened_date: poll.startedAt,
    open: Number(poll.started),
    options: poll.options.map((option) => ({
      option: option.option,
      poll_option_id: option.id.toString(),
      answer_percentage: option.count.toString(),
    })),
  }
}

export const PollCardResults = memo(PollResults)
export const PollCardNoPoll = memo(NoPoll)
export default memo(PollCard)
